// Service Subscription Framework API（Sprint 4-pre · 3-role）
import axios from '../api/axios';

export interface Subscription {
  id: number;
  tenant_org_id: number;
  org_name?: string | null;
  service_code: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended' | 'expired';
  requested_at?: string | null;
  approved_at?: string | null;
  quota_config: Record<string, unknown>;
  billing_start?: string | null;
  billing_end?: string | null;
  rejection_reason?: string | null;
  rejected_at?: string | null;
  rejected_by?: number | null;
  notes?: string | null;
}

// 4-pre-c · 授權 provider
export interface SubProvider {
  provider_id: number;
  provider_code: string;
  provider_name: string;
  key_source: string;
  is_owner?: boolean;
  is_active_for_tenant?: boolean;
  granted_at?: string | null;
  share_allowed?: boolean;
  provided_by_org_id?: number | null;
}

export interface UserQuotaRow {
  user_id: number;
  username: string;
  account: string;
  service_code: string;
  month: string;
  base_quota_tokens: number;
  override_quota: number | null;
  effective_limit: number;
  used_tokens: number;
  used_calls: number;
  blocked: boolean;              // E4 D17：使用者是否被停用此服務
  block_reason: string | null;
}

export interface TenantUsage {
  month: string;
  total_tokens: number;
  items: { user_id: number; username: string; service_code: string; used_tokens: number; used_calls: number; effective_limit: number }[];
  top_users: { user_id: number; username: string; service_code: string; used_tokens: number }[];
}

export interface MyUsage {
  month: string;
  services: {
    service_code: string;
    used_tokens: number;
    used_calls: number;
    limit: number;
    remaining: number | null;
    used_pct: number | null;
  }[];
}

export interface MyUsageHistory { month: string; used_tokens: number; used_calls: number }

// 4-pre-b · 可申請服務 registry（system_codes code_type=subscription_service）
export interface ServiceCode {
  code: string;
  code_name: Record<string, string>;
  is_active: boolean;
  order?: number;
}

// 走既有 /api/system_codes/type/{code_type}（active_only 預設 true）· 過濾 _registry marker
export const listAvailableServices = (): Promise<ServiceCode[]> =>
  axios.get<ServiceCode[]>('/api/system_codes/type/subscription_service', { params: { active_only: true } })
    .then(r => (r.data || []).filter(c => c.code !== '_registry'));

// service_code → 當前語系顯示名（fallback en → raw code）
export const resolveServiceLabel = (code: string, services: ServiceCode[], lang: string): string => {
  const c = services.find(x => x.code === code);
  if (!c || !c.code_name) return code;
  return c.code_name[lang] || c.code_name['zh-TW'] || c.code_name['en'] || code;
};

// ── SYSTEM_ADMIN ──────────────────────────────────────────────────────────────
const ADMIN = '/api/system/subscriptions';
export const listPending = () => axios.get<Subscription[]>(`${ADMIN}/pending`).then(r => r.data);
export const listOverview = () => axios.get<Subscription[]>(`${ADMIN}/overview`).then(r => r.data);
// 4-pre-c · 核准必附 provider_ids（≥1）· POST
export const approveSubscription = (subId: number, providerIds: number[], defaultPerUser: number, billingStart?: string) =>
  axios.post<Subscription>(`${ADMIN}/${subId}/approve`,
    { provider_ids: providerIds, default_per_user: defaultPerUser, billing_start: billingStart || null }).then(r => r.data);
export const rejectSubscription = (subId: number, reason: string) =>
  axios.post<Subscription>(`${ADMIN}/${subId}/reject`, { rejection_reason: reason }).then(r => r.data);
export const listSubProviders = (subId: number) =>
  axios.get<SubProvider[]>(`${ADMIN}/${subId}/providers`).then(r => r.data);
export const availableProviders = (subId: number) =>
  axios.get<SubProvider[]>(`${ADMIN}/${subId}/available-providers`).then(r => r.data);
export const addSubProvider = (subId: number, providerId: number) =>
  axios.post(`${ADMIN}/${subId}/providers/${providerId}`).then(r => r.data);
export const removeSubProvider = (subId: number, providerId: number) =>
  axios.delete(`${ADMIN}/${subId}/providers/${providerId}`).then(r => r.data);
export const billingCsvUrl = (orgId: number, year: number, month: number) =>
  `${ADMIN}/billing/${orgId}/${year}/${month}`;

// ── Tenant admin ──────────────────────────────────────────────────────────────
const TENANT = '/api/ee/subscriptions';
export const requestSubscription = (serviceCode: string, notes?: string) =>
  axios.post<Subscription>(`${TENANT}/request`, { service_code: serviceCode, notes: notes || null }).then(r => r.data);
export const listMine = () => axios.get<Subscription[]>(`${TENANT}/mine`).then(r => r.data);
export const listUserQuotas = (serviceCode: string, month?: string) =>
  axios.get<UserQuotaRow[]>(`${TENANT}/user-quotas`, { params: { service_code: serviceCode, month } }).then(r => r.data);
export const setUserOverride = (userId: number, serviceCode: string, overrideQuota: number | null, month?: string) =>
  axios.patch(`${TENANT}/user-quotas/${userId}/${serviceCode}`, { override_quota: overrideQuota }, { params: { month } }).then(r => r.data);
// E4 D17 · 使用者服務停用 / 取消停用
export const blockUserService = (userId: number, serviceCode: string, reason?: string) =>
  axios.put(`${TENANT}/user-services/${userId}/${serviceCode}/block`, { reason: reason || null }).then(r => r.data);
export const unblockUserService = (userId: number, serviceCode: string) =>
  axios.delete(`${TENANT}/user-services/${userId}/${serviceCode}/block`).then(r => r.data);
export const tenantUserUsage = (month?: string) =>
  axios.get<TenantUsage>(`${TENANT}/user-usage`, { params: { month } }).then(r => r.data);

// ── User (self) ───────────────────────────────────────────────────────────────
const MY = '/api/ee/my-ai';
export const myUsage = (month?: string) => axios.get<MyUsage>(`${MY}/usage`, { params: { month } }).then(r => r.data);
export const myUsageHistory = (months = 6) => axios.get<MyUsageHistory[]>(`${MY}/usage/history`, { params: { months } }).then(r => r.data);
// 我的租戶對某服務是否已訂閱啟用（前端 gate render）
export const checkServiceActive = (serviceCode: string): Promise<boolean> =>
  axios.get<{ active: boolean }>(`${MY}/service-active/${serviceCode}`).then(r => !!r.data?.active).catch(() => false);
